import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageShell from "../components/layout/PageShell";
import CinematicHero, { HeroOverlay } from "../components/layout/CinematicHero";
import Container from "../components/primitives/Container";
import Section from "../components/primitives/Section";
import Reveal from "../components/primitives/Reveal";
import SeoHead from "../components/seo/SeoHead";
import LivingImage from "../components/editorial/LivingImage";
import { IMAGES } from "../content/images";
import { INSIGHTS_META } from "../content/site";

const API = process.env.REACT_APP_BACKEND_URL;
const FEATURED_SLUG = "letter-no-v";

const Insights = () => {
  const [letters, setLetters] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let abort = false;
    fetch(`${API}/api/insights`)
      .then((r) => r.json())
      .then((data) => {
        if (!abort) setLetters(Array.isArray(data) ? data : []);
      })
      .catch((e) => {
        if (!abort) setError(String(e));
      });

    // Fetch the featured letter's detail so we can render a second declarative
    // sentence beneath the excerpt on the featured card.
    fetch(`${API}/api/insights/${FEATURED_SLUG}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!abort && data) setFeatured(data);
      })
      .catch(() => {});

    return () => {
      abort = true;
    };
  }, []);

  // The featured letter row (metadata) — pulled from the list so we don't
  // depend on the detail endpoint for title / dateline / excerpt.
  const featuredMeta =
    letters.find((l) => l.slug === FEATURED_SLUG) ||
    (featured
      ? {
          slug: featured.slug,
          number: featured.number,
          title: featured.title,
          season: featured.season,
          dateline: featured.dateline,
          excerpt: featured.excerpt,
        }
      : null);

  // Second declarative sentence — pulled from the letter's first paragraph.
  const featuredExtension = (() => {
    if (!featured || !Array.isArray(featured.body)) return "";
    const firstP = featured.body.find((b) => b && b.type === "p");
    if (!firstP || typeof firstP.text !== "string") return "";
    // Sentence-split on a period followed by whitespace + capital letter.
    // We want the second sentence, since sentence one is the excerpt.
    const parts = firstP.text.split(/(?<=\.)\s+(?=[A-Z(])/);
    return parts[1] ? parts[1].trim() : "";
  })();

  const rows = letters.filter((l) => l.slug !== FEATURED_SLUG);

  return (
    <PageShell>
      <SeoHead
        title="Letters"
        description="Reflections on capital, judgment, and the long term. A series of institutional letters from Quantum US Capital Partners."
        path="/letters"
        ogImage={IMAGES.letters_hero_desk}
      />

      {/* Editorial hero — dark still-life anchor. Fades into the dark featured section. */}
      <CinematicHero
        image={IMAGES.letters_hero_desk}
        imageAlt="Editorial still life — a fountain pen and folded letter, deep-navy graded."
        eager
        fadeTone="dark"
        data-testid="letters-hero"
      >
        <HeroOverlay
          eyebrow={INSIGHTS_META.hero.eyebrow}
          title={INSIGHTS_META.hero.headline}
          statement={INSIGHTS_META.hero.subtitle}
          size="short"
        />
      </CinematicHero>

      {/* Featured letter — dark navy surface, immediately continues from hero. */}
      <Section
        tone="dark"
        className="pt-24 md:pt-24 lg:pt-24 pb-6 md:pb-8 lg:pb-8"
        data-testid="letters-featured-section"
        data-section-tone="dark"
      >
        <Container>
          {featuredMeta ? (
            <Reveal>
              <Link
                to={`/letters/${featuredMeta.slug}`}
                data-testid="featured-letter"
                className="group grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center"
              >
                {/* Left column — landscape 4:3 image, muted + navy tint */}
                <div className="md:col-span-5">
                  <div className="relative w-full bg-navy" style={{ aspectRatio: "4 / 3" }}>
                    <LivingImage
                      src={IMAGES.letter_uncertain}
                      alt="Featured letter — fogged skyline visual accompanying Capital Allocation in Uncertain Markets."
                      className="absolute inset-0"
                      variant="editorial"
                      imgStyle={{ filter: "saturate(0.85) brightness(0.95)" }}
                      data-testid="featured-letter-image"
                    />
                    {/* Subtle navy overlay to unify with dark surface */}
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 pointer-events-none z-10"
                      style={{ backgroundColor: "rgba(11,19,43,0.12)" }}
                    />
                  </div>
                </div>

                {/* Right column — editorial content, warm-off-white */}
                <div className="md:col-span-7">
                  <p
                    className="font-sans uppercase text-gold"
                    style={{ fontSize: "11px", letterSpacing: "0.22em", fontWeight: 500 }}
                    data-testid="featured-letter-eyebrow"
                  >
                    Featured · Letter No. {featuredMeta.number}
                  </p>
                  <p
                    className="mt-3 font-serif italic text-offwhite/60"
                    style={{ fontSize: "clamp(14px, 1.1vw, 16px)", lineHeight: 1.4 }}
                    data-testid="featured-letter-dateline"
                  >
                    {featuredMeta.season || featuredMeta.dateline}
                  </p>
                  <h2
                    className="mt-8 font-serif font-normal text-offwhite group-hover:text-gold transition-colors duration-300 text-balance"
                    style={{
                      fontSize: "clamp(36px, 4.5vw, 64px)",
                      lineHeight: 1.05,
                      letterSpacing: "-0.01em",
                    }}
                    data-testid="featured-letter-title"
                  >
                    {featuredMeta.title}
                  </h2>
                  <p
                    className="mt-6 font-sans text-offwhite/80 max-w-[60ch]"
                    style={{ fontSize: "clamp(16px, 1.1vw, 17px)", lineHeight: 1.7 }}
                    data-testid="featured-letter-excerpt"
                  >
                    {featuredMeta.excerpt}
                    {featuredExtension ? " " + featuredExtension : ""}
                  </p>
                  <span
                    className="mt-8 inline-flex items-center gap-3 font-sans uppercase text-offwhite"
                    style={{ fontSize: "11px", letterSpacing: "0.22em", fontWeight: 500 }}
                  >
                    <span className="relative">
                      Read letter
                      <span
                        aria-hidden="true"
                        className="absolute left-0 right-0 -bottom-1 h-px bg-gold scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300 ease-out"
                      />
                    </span>
                    <span
                      aria-hidden="true"
                      className="inline-block transition-transform duration-300 group-hover:translate-x-1"
                    >
                      →
                    </span>
                  </span>
                </div>
              </Link>
            </Reveal>
          ) : null}
        </Container>
      </Section>

      {/* Divider + "More letters" eyebrow + remaining rows — continuous dark navy through to footer. */}
      <Section
        tone="dark"
        className="pt-6 md:pt-8 lg:pt-8 pb-20 md:pb-28 lg:pb-28"
        data-testid="insights-index"
        data-section-tone="dark"
      >
        <Container>
          <div className="max-w-5xl mx-auto">
            <span
              aria-hidden="true"
              className="block h-px bg-offwhite/15"
              style={{ marginTop: 0 }}
            />
            <p
              className="mt-12 font-sans uppercase text-offwhite/55"
              style={{ fontSize: "11px", letterSpacing: "0.22em", fontWeight: 500 }}
              data-testid="more-letters-eyebrow"
            >
              More Letters
            </p>

            <ul className="mt-12 divide-y divide-offwhite/15 border-t border-offwhite/15">
              {error ? (
                <li className="py-16 text-center text-offwhite/60 font-sans text-sm">
                  Unable to load letters. Please try again.
                </li>
              ) : null}
              {rows.map((l, i) => (
                <li key={l.slug}>
                  <Reveal delay={i * 60}>
                    <Link
                      to={`/letters/${l.slug}`}
                      data-testid={`letter-row-${l.slug}`}
                      className="group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-14 md:py-20 transition-colors"
                    >
                      <div className="md:col-span-3">
                        <span
                          className="font-sans uppercase text-gold"
                          style={{ fontSize: "11px", letterSpacing: "0.22em" }}
                        >
                          Letter No. {l.number}
                        </span>
                        <p
                          className="mt-3 font-sans uppercase text-offwhite/55"
                          style={{ fontSize: "11px", letterSpacing: "0.18em" }}
                        >
                          {l.season || l.dateline}
                        </p>
                      </div>
                      <div className="md:col-span-9">
                        <h3
                          className="font-serif font-normal text-offwhite group-hover:text-gold transition-colors duration-300 text-balance"
                          style={{
                            fontSize: "clamp(28px, 3vw, 44px)",
                            lineHeight: 1.12,
                            letterSpacing: "-0.005em",
                          }}
                        >
                          {l.title}
                        </h3>
                        <p className="mt-5 font-sans text-[16px] leading-[1.7] text-offwhite/70 max-w-[58ch]">
                          {l.excerpt}
                        </p>
                        <span
                          className="mt-7 inline-flex items-center gap-3 font-sans uppercase text-offwhite/85 group-hover:text-gold transition-colors duration-300"
                          style={{ fontSize: "11px", letterSpacing: "0.22em" }}
                        >
                          <span>Read letter</span>
                          <span
                            aria-hidden="true"
                            className="inline-block transition-transform duration-300 group-hover:translate-x-1"
                          >
                            →
                          </span>
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                </li>
              ))}
            </ul>
          </div>
        </Container>
      </Section>
    </PageShell>
  );
};

export default Insights;
